import { isSeatOccupied } from 'utils/purchase/seats';

/**
 * Get unique adjacent seats.
 * @param {Array} seatsWithAdjacent - adjacent seats array as [[1,2],[3,4]]
 * @returns {Set} - unique adjacent seats count
 */
const getUniqueAdjacentSeats = (seatsWithAdjacent) => {
  const uniquePairs = new Set();

  seatsWithAdjacent.forEach((seat) => {
    const [seatA, seatB] = [seat.number, seat.adjacentSeats.numbers[0]];
    if (!seatA || !seatB) return uniquePairs;
    const pair = [seatA, seatB].sort((a, b) => a - b).join(',');
    uniquePairs.add(pair);
  });

  return uniquePairs;
};

/**
 * This function determines if the toggle should be turn on or off.
 * Returns true only if we have two adjacent seats picked
 * @param {Array} selectedSeatFiltered
 * @returns {boolean}
 */
const shouldTurnOnToggle = (selectedSeatFiltered) => {
  const hasOneAdjacentSeatPicked =
    selectedSeatFiltered.some((seat) => {
      return seat.isPickedAsAdjacent;
    }) &&
    selectedSeatFiltered.some((seat) => {
      return seat.isAdjacentPicked;
    });
  return hasOneAdjacentSeatPicked;
};

/**
 * This function determines the visibility of an adjacent card based on the selected seats by calculating the unique pairs of adjacent seats.
 * @param {Array} seatsSelected - Array of selected seats
 * @param {Object} options - Additional options
 * @param {boolean} options.isExchange - Whether we are in exchange mode
 * @returns {Object} - Object containing visibility and toggle state
 */
const calculateAdjacentCardVisibility = (seatsSelected, options = {}) => {
  const { isExchange = false } = options;
  // Filter seats with adjacent seat capability
  const selectedSeatFiltered = seatsSelected.filter((selectedSeat) => selectedSeat.adjacentSeats);
  // Get unique pairs of adjacent seats
  const uniques = getUniqueAdjacentSeats(selectedSeatFiltered);
  // Check if there are seats without adjacent seat capability
  const hasNonAdjacentSeats = seatsSelected.some((seat) => !seat.adjacentSeats);
  // In exchange mode, if there's a mix of adjacent and non-adjacent seats,
  // don't show the adjacent seat card
  // Determine card visibility
  const isAdjacentCardVisible =
    uniques.size === 1 && !(isExchange && hasNonAdjacentSeats && selectedSeatFiltered.length > 0);
  const shouldTurnOnToggleValue = shouldTurnOnToggle(selectedSeatFiltered);
  return {
    isAdjacentCardVisible,
    shouldTurnOnToggleValue,
  };
};

/**
 * Prepares a seat to be recognized as part of an adjacent seat grouping.
 * Sets the corresponding property based on the seat type.
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.seat - The seat to prepare
 * @param {string} params.type - The seat type ('parent' or 'adjacent')
 * @returns {Object} - The prepared seat with the corresponding property
 */
export const prepareAdjacentSeat = ({ seat, type }) => {
  if (!seat) {
    return seat;
  }

  // Clone the seat to avoid modifying the original
  const updatedSeat = { ...seat };

  // Set the corresponding property based on the type
  if (type === 'parent') {
    updatedSeat.isAdjacentPicked = true;
  } else if (type === 'adjacent') {
    updatedSeat.isPickedAsAdjacent = true;
  }

  return updatedSeat;
};

/**
 * Checks if two seats form an adjacent pair by verifying if they reference each other
 * in their adjacentSeats property.
 *
 * @param {Object} seat1 - First seat object
 * @param {Object} seat2 - Second seat object
 * @returns {boolean} - True if the seats form an adjacent pair, false otherwise
 */
export const areSeatsAdjacent = (seat1, seat2) => {
  // Check if both seats have adjacentSeats property
  if (!seat1?.adjacentSeats?.numbers || !seat2?.adjacentSeats?.numbers) return false;

  // Check if they reference each other
  return (
    seat1.adjacentSeats.numbers.includes(seat2.number) &&
    seat2.adjacentSeats.numbers.includes(seat1.number)
  );
};

/**
 * Determines if the selected seats form a valid adjacent pair.
 *
 * @param {Array} selectedSeats - Array of selected seat objects
 * @returns {boolean} - True if the selected seats form a valid adjacent pair, false otherwise
 */
export const hasValidAdjacentPair = (selectedSeats) => {
  // If we don't have exactly 2 seats, they can't form a valid adjacent pair
  if (!selectedSeats || selectedSeats.length !== 2) return false;

  const [seat1, seat2] = selectedSeats;

  // Check if the seats form an adjacent pair
  return areSeatsAdjacent(seat1, seat2);
};

/**
 * Finds the adjacent seat for a given seat in the bus diagram
 * @param {Object} params - The function parameters
 * @param {Object} params.seats - The seats object from the bus diagram
 * @param {Object} params.seat - The seat to find its adjacent
 * @returns {Object|null} - The adjacent seat object or null if not found
 */
export const findAdjacentSeatPair = ({ seats, seat }) => {
  if (
    !seat ||
    !seat.adjacentSeats ||
    !seat.adjacentSeats.numbers ||
    !seat.adjacentSeats.numbers.length
  ) {
    return null;
  }

  const adjacentSeatNumber = seat.adjacentSeats.numbers[0];
  const adjacentSeat = Object.values(seats).find(
    (s) => s.number === adjacentSeatNumber && !isSeatOccupied(s),
  );

  return adjacentSeat || null;
};

/**
 * Checks if the adjacent seat is already picked as adjacent
 * @param {Array} selectedSeats - The array of selected seats
 * @param {Number} adjacentSeatNumber - The adjacent seat number
 * @returns {Boolean} - True if the adjacent seat is already picked
 */
export const checkIfAdjacentPicked = (selectedSeats, adjacentSeatNumber) => {
  const isAlreadyPicked = selectedSeats.some(
    (seat) => seat.number === adjacentSeatNumber && seat.isPickedAsAdjacent,
  );
  return isAlreadyPicked;
};

/**
 * Checks if the adjacent seat is already selected
 * @param {Array} selectedSeats - The array of selected seats
 * @param {Number} adjacentSeatNumber - The adjacent seat number
 * @returns {Boolean} - True if the adjacent seat is already selected
 */
export const checkIfAdjacentSelected = (selectedSeats, adjacentSeatNumber) => {
  const isAlreadySelected = selectedSeats.some((seat) => seat.number === adjacentSeatNumber);
  return isAlreadySelected;
};

/**
 * Get a seat from the bus diagram by seat number and level.
 * @param {Object} params - The function parameters.
 * @param {Object} params.seats - The seats object.
 * @param {string|number} params.seatNumber - The seat number to find.
 * @param {string|number} params.seatLevel - The seat level (optional).
 * @returns {Object} - The seat object or empty object if not found.
 */
export const getSeatFromBusDiagram = ({ seats, seatNumber, seatLevel }) => {
  // ? If seat level exists, recreate diagram taking that value into account
  const seatFromDiagram = seatLevel
    ? Object.values(seats).find(
        (seat) => seat.seatLevel === seatLevel && seat.number === seatNumber,
      )
    : seats[seatNumber];
  if (!seatFromDiagram) return {};

  return seatFromDiagram;
};

/**
 * Checks if there are any adjacent seats in the provided tickets.
 * @param {Array} tickets - Array of ticket objects.
 * @returns {boolean} - True if any ticket has an adjacent seat, false otherwise.
 */
export const hasAdjacentSeatsInTickets = (tickets = []) => {
  return tickets.some((ticket) => ticket.adjacentSeat || ticket.isAdjacentSeat);
};

export { getUniqueAdjacentSeats, shouldTurnOnToggle };
export default calculateAdjacentCardVisibility;
